/*
 * Shared stereo-audio validation for Uppy uploaders.
 *
 * Why this exists:
 * - Uploaders use `autoProceed: true`.
 * - Channel detection is asynchronous.
 * - If validation runs in `file-added`, upload can start before validation finishes.
 *
 * This module registers an Uppy preprocessor so channel validation finishes first.
 * Non-stereo (or unreadable) files are removed and upload is hard-failed with
 * a user-facing error message.
 */
const DEFAULT_ERROR_TIMEOUT_MS = 5000;
const DEFAULT_SLICE_BYTES = 1 * 1024 * 1024;
export const STEREO_VALIDATION_ERROR_CODE = "STEREO_VALIDATION_FAILED";
const FORCE_SKIP_CHANNEL_VALIDATION = true;

// Creates an AudioContext compatible with both modern and older WebKit browsers.
function createDefaultAudioContext() {
  return new (window.AudioContext || window.webkitAudioContext)();
}

// Normalizes decodeAudioData across callback and promise-based browser implementations.
function decodeAudioData(audioContext, arrayBuffer) {
  return new Promise((resolve, reject) => {
    const maybePromise = audioContext.decodeAudioData(arrayBuffer, resolve, reject);
    if (maybePromise && typeof maybePromise.then === "function") {
      maybePromise.then(resolve).catch(reject);
    }
  });
}

// Reads a blob/file into an ArrayBuffer with a FileReader fallback.
function readBlobAsArrayBuffer(blob, errorMessage) {
  if (blob && typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => reject(new Error(errorMessage));
    reader.readAsArrayBuffer(blob);
  });
}

// Reads only the first N bytes for fast channel detection before full upload.
function readSliceAsArrayBuffer(fileData, maxBytesForAnalysis) {
  if (!fileData || typeof fileData.slice !== "function") {
    return Promise.reject(new Error("Missing file data"));
  }

  const slice = fileData.slice(0, maxBytesForAnalysis);
  return readBlobAsArrayBuffer(slice, "Failed to read audio file slice");
}

// Reads the full file as a fallback when the initial slice is not decodable.
function readFullAsArrayBuffer(fileData) {
  if (!fileData) {
    return Promise.reject(new Error("Missing file data"));
  }

  if (typeof fileData.arrayBuffer === "function") {
    return fileData.arrayBuffer();
  }

  if (typeof fileData.slice === "function") {
    return readBlobAsArrayBuffer(fileData.slice(0), "Failed to read audio file");
  }

  return Promise.reject(new Error("Unsupported file data"));
}

// Builds a clear user-facing validation message for invalid channel counts.
function formatStereoValidationError(fileName, channelCount) {
  if (channelCount === null) {
    return `${fileName}: We could not determine the channel count. Please upload stereo audio only.`;
  }

  if (channelCount === 1) {
    return `${fileName}: This file is mono. Please upload stereo audio only.`;
  }

  return `${fileName}: Detected ${channelCount} channels. Only stereo (2 channels) is supported.`;
}

function buildStereoValidationError(message) {
  const error = new Error(message);
  error.name = "StereoValidationError";
  error.code = STEREO_VALIDATION_ERROR_CODE;
  error.isStereoValidationError = true;
  return error;
}

export function isStereoValidationFailureError(error) {
  if (!error) {
    return false;
  }

  return (
    error.isStereoValidationError === true ||
    error.code === STEREO_VALIDATION_ERROR_CODE ||
    error.name === "StereoValidationError"
  );
}

// Decodes a small audio slice and returns channel count, or null if unavailable.
async function getAudioChannelCount(file, options = {}) {
  const {
    createAudioContext = createDefaultAudioContext,
    maxBytesForAnalysis = DEFAULT_SLICE_BYTES,
    enableFullFileFallback = true,
  } = options;

  const decodeChannelCount = async (audioBuffer) => {
    const audioContext = createAudioContext();
    try {
      const decoded = await decodeAudioData(audioContext, audioBuffer);
      return Number.isInteger(decoded.numberOfChannels) ? decoded.numberOfChannels : null;
    } finally {
      if (audioContext && typeof audioContext.close === "function") {
        try {
          await audioContext.close();
        } catch (error) {
          // Closing the context is best-effort; failure should not block validation.
        }
      }
    }
  };

  try {
    const audioBuffer = await readSliceAsArrayBuffer(file.data, maxBytesForAnalysis);
    return await decodeChannelCount(audioBuffer);
  } catch (sliceDecodeError) {
    if (!enableFullFileFallback) {
      throw sliceDecodeError;
    }

    const fullAudioBuffer = await readFullAsArrayBuffer(file.data);
    return await decodeChannelCount(fullAudioBuffer);
  }
}

// Validates that a single file is stereo; returns a structured result for callers.
export async function validateStereoAudioFile(file, options = {}) {
  // Hotfix: temporarily bypass channel decoding everywhere.
  // This prevents the browser from loading large audio files into memory.
  if (FORCE_SKIP_CHANNEL_VALIDATION || options.skipChannelValidation) {
    return { valid: true, channelCount: null };
  }

  try {
    const channelCount = await getAudioChannelCount(file, options);
    if (channelCount !== 2) {
      return {
        valid: false,
        channelCount,
        message: formatStereoValidationError(file.name, channelCount),
      };
    }

    return { valid: true, channelCount: 2 };
  } catch (error) {
    return {
      valid: false,
      channelCount: null,
      message: `${file.name}: Unable to process audio channels. Please upload a valid stereo file.`,
    };
  }
}

// Registers an Uppy preprocessor that hard-fails upload for non-stereo audio.
export function registerStereoAudioPreProcessor(uppy, options = {}) {
  const { errorTimeoutMs = DEFAULT_ERROR_TIMEOUT_MS } = options;

  // Use a preprocessor so autoProceed uploads do not start before async channel validation finishes.
  uppy.addPreProcessor(async (fileIDs) => {
    for (const fileID of fileIDs) {
      const file = uppy.getFile(fileID);
      if (!file || !file.data) {
        continue;
      }

      const validationResult = await validateStereoAudioFile(file, options);
      if (!validationResult.valid) {
        uppy.info(validationResult.message, "error", errorTimeoutMs);
        uppy.removeFile(fileID);
        throw buildStereoValidationError(validationResult.message);
      }
    }
  });
}
